import { Pressable } from 'react-native';
import { Text, XStack } from 'tamagui';
import { TAG_CONFIG, useThemeColors, Icon } from '@kiips/ui';
import type { CardTag } from '@my-fullstack-app/itinerary-types';

const TAG_OPTIONS = ['important', 'reach-goal', 'missing-info'] as const;

interface TagPickerRowProps {
  /** Currently-selected tag (undefined = none). */
  selected: CardTag | undefined;
  /** Toggle a tag — pass `undefined` to clear. */
  onChange: (tag: CardTag | undefined) => void;
  /** When false, render disabled chips (no tap handlers). */
  canEdit: boolean;
}

/**
 * Three-chip tag picker — same UX as the row inside /place/[id]'s edit
 * panel. Tap an active tag to clear it; tap an inactive tag to set it.
 * Caller wires `onChange` to `useItineraryStore.setTag(tripId, entityId, ...)`.
 */
export function TagPickerRow({ selected, onChange, canEdit }: TagPickerRowProps) {
  const theme = useThemeColors();
  return (
    <XStack flexWrap="wrap" gap={6}>
      {TAG_OPTIONS.map((t) => {
        const cfg = TAG_CONFIG[t];
        const active = selected === t;
        return (
          <Pressable
            key={t}
            onPress={canEdit ? () => onChange(active ? undefined : t) : undefined}
            disabled={!canEdit}
            accessibilityRole="button"
            accessibilityState={{ selected: active }}
            accessibilityLabel={`${cfg.label} tag`}
            style={({ pressed }) => [
              {
                flexDirection: 'row',
                alignItems: 'center',
                gap: 6,
                paddingHorizontal: 10,
                paddingVertical: 6,
                borderRadius: 14,
                backgroundColor: active ? cfg.color + '24' : theme.surfaceCanvas,
                borderWidth: 1,
                borderColor: active ? cfg.color : 'transparent',
              },
              pressed && canEdit ? { transform: [{ scale: 0.97 }] } : null,
            ]}
          >
            <Icon
              name={cfg.icon}
              size={14}
              color={active ? cfg.color : theme.textSubdued}
            />
            <Text
              fontSize={12}
              fontWeight="600"
              style={{ color: active ? cfg.color : theme.textOnSurface }}
            >
              {cfg.label}
            </Text>
          </Pressable>
        );
      })}
    </XStack>
  );
}
